import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Card } from './Card';

/**
 * `accent` for an improvement, `warn` when the number itself is the problem
 * ("Open rapporten" is amber), `neutral` for a plain fact.
 */
export type KpiTone = 'neutral' | 'accent' | 'warn' | 'danger';

const valueTone: Record<KpiTone, string> = {
    neutral: 'text-[var(--text-primary)]',
    accent: 'text-[var(--accent)]',
    warn: 'text-[var(--warn)]',
    danger: 'text-[var(--danger)]',
};

const deltaTone: Record<KpiTone, string> = {
    neutral: 'text-[var(--text-tertiary-safe)]',
    accent: 'text-[var(--accent)]',
    warn: 'text-[var(--warn)]',
    danger: 'text-[var(--danger)]',
};

export type KpiCardProps = Omit<ComponentPropsWithRef<'div'>, 'children' | 'title'> & {
    /** 12px `--text-tertiary-safe`. A noun: "Gem. doorlooptijd rapport". */
    label: ReactNode;
    /** Space Grotesk 600, 30px. */
    value: ReactNode;
    /** Smaller trailing unit inside the value ("3 u 12m" — "u 12m" is the unit). */
    unit?: ReactNode;
    /**
     * The follow-up line. A number without a follow-up is decoration (principle 7):
     * either a delta or a waiting time, never nothing.
     */
    delta?: ReactNode;
    tone?: KpiTone;
    deltaToneOverride?: KpiTone;
    /** 30px default; 24/22/20 for the smaller figure cards. */
    size?: 30 | 24 | 22 | 20;
};

const valueSize: Record<NonNullable<KpiCardProps['size']>, string> = {
    30: 'text-[30px]',
    24: 'text-[24px]',
    22: 'text-[22px]',
    20: 'text-[20px]',
};

/**
 * Dashboard KPI. Maximum four per screen, and report turnaround always sits in the
 * first two — that is the promise of the product (principle 7).
 */
export function KpiCard({
    label,
    value,
    unit,
    delta,
    tone = 'neutral',
    deltaToneOverride,
    size = 30,
    className,
    ...rest
}: KpiCardProps) {
    return (
        <Card padding="md" className={className} {...rest}>
            <div className="mb-2 text-[12px] leading-[1.35] text-[var(--text-tertiary-safe)]">
                {label}
            </div>
            <div
                className={cn(
                    'font-[family-name:var(--font-display)] font-semibold tracking-[-0.02em] leading-[1.1]',
                    valueSize[size],
                    valueTone[tone],
                )}
            >
                {value}
                {unit ? (
                    <span className="text-[16px] font-semibold text-[var(--text-tertiary-safe)]">
                        {unit}
                    </span>
                ) : null}
            </div>
            {delta ? (
                <div
                    className={cn(
                        'mt-[6px] text-[12px] leading-[1.35]',
                        deltaTone[deltaToneOverride ?? tone],
                    )}
                >
                    {delta}
                </div>
            ) : null}
        </Card>
    );
}
